import { IconButton, makeStyles, useMediaQuery } from "@material-ui/core"
import { ArrowLeft, ArrowRight } from "@material-ui/icons"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { Apresentacao } from "./components/Apresentacao"
import { Bairros } from "./components/Bairros"
import { ClienteNovos } from "./components/ClienteNovos"
import { Faturamento } from "./components/Faturamento"
import { Final } from "./components/Final"
import { Pedidos } from "./components/Pedidos"
import { ProdutosMaisVendidos } from "./components/ProdutosMaisVendidos"
import { Resumo } from "./components/Resumo"
import { TopClientes } from "./components/TopClientes"

const useStyles = makeStyles(() => ({
    root: {
        backgroundColor: "#3B3B3B",
        width: "100vw",
        height: "100dvh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    frame: {
        width: "100%",
        // maxWidth: 300,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "1.5rem",
        "@media (max-width: 900px)": {
            width: "100vw",
            maxWidth: "100vw",
        },
    },
    phone: {
        position: "relative",
        width: "25vw",
        maxWidth: "390px",
        minWidth: 380,
        height: "82dvh",
        maxHeight: "693px",
        minHeight: "693px",
        backgroundColor: "#3B3B3B",
        borderRadius: "1.2rem",
        overflow: "hidden",
        boxShadow: "0 8px 28px rgba(0,0,0,0.35)",
        userSelect: "none",
        touchAction: "none",
        "@media (max-width: 900px)": {
            width: "100vw",
            maxWidth: "100vw",
            height: "100dvh",
            maxHeight: "100dvh",
            borderRadius: "0px",
        },
    },
    navBtn: {
        backgroundColor: "rgba(128, 128, 128, 0.5)",
        color: "white",
    },
    overlayTop: {
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        padding: "12px 12px 8px",
        background: "linear-gradient( to bottom, rgba(0,0,0,0.55), rgba(0,0,0,0))",
        display: "grid",
        gap: 6,
        zIndex: 99999,
    },
    barras: {
        display: "grid",
        gridAutoFlow: "column",
        gap: 6,
    },
    barWrap: {
        background: "rgba(255,255,255,0.35)",
        borderRadius: 999,
        overflow: "hidden",
        height: 4,
    },
    progresso: {
        height: 4,
        background: "white",
        transition: "width 80ms linear",
    },
    tapZones: {
        position: "absolute",
        inset: 0,
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
    },
    leftZone: { cursor: "pointer" },
    rightZone: { cursor: "pointer" },
    content: {
        position: "absolute",
        inset: 0,
        display: "grid",
        placeItems: "center",
        // padding: 24,
    },
}))

export function ResultadosAnual() {
    const classes = useStyles()
    const isMobile = useMediaQuery("(max-width: 900px)")

    const telas = useMemo(() => [0, 1, 2, 3, 4, 5, 6, 7, 8], [])

    const [index, setIndex] = useState(0)
    const [pausado, setPausado] = useState(false)
    const duracaoMs = 4500
    const tickMs = 80

    const [progresso, setProgresso] = useState(0)
    const timerRef = useRef(null as any)
    const touchStartX = useRef<number | null>(null)

    useEffect(() => {
        if (pausado) return

        timerRef.current && clearInterval(timerRef.current)
        setProgresso(0)

        const startedAt = Date.now()
        timerRef.current = setInterval(() => {
            const elapsed = Date.now() - startedAt
            const p = Math.min(1, elapsed / duracaoMs)
            setProgresso(p)
            if (p >= 1) {
                goNext()
            }
        }, tickMs)

        return () => timerRef.current && clearInterval(timerRef.current)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [index, pausado])

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "ArrowRight") goNext()
            if (e.key === "ArrowLeft") goPrev()
            if (e.code === "Space") setPausado(p => !p)
        }
        window.addEventListener("keydown", onKey)
        return () => window.removeEventListener("keydown", onKey)
    }, [])

    const goNext = () => setIndex(i => (i + 1) % telas.length)
    const goPrev = () => setIndex(i => (i - 1 + telas.length) % telas.length)

    const barras = telas.map((_, i) => {
        const value = i < index ? 1 : i === index ? progresso : 0
        return (
            <div key={i} className={classes.barWrap}>
                <div className={classes.progresso} style={{ width: `${value * 100}%` }} />
            </div>
        )
    })

    const onTouchStart = (e: React.TouchEvent) => {
        touchStartX.current = e.touches[0].clientX
        setPausado(true)
    }
    const onTouchEnd = (e: React.TouchEvent) => {
        const sx = touchStartX.current
        setPausado(false)
        if (sx == null) return
        const dx = e.changedTouches[0].clientX - sx
        if (Math.abs(dx) > 30) {
            dx < 0 ? goNext() : goPrev()
        }
        touchStartX.current = null
    }

    type Telas = {
        [key: number]: React.ReactNode
    }

    const telasRender: Telas = {
        0: <Apresentacao />,
        1: <Pedidos />,
        2: <ProdutosMaisVendidos />,
        3: <Faturamento />,
        4: <Bairros />,
        5: <ClienteNovos />,
        6: <TopClientes />,
        7: <Resumo />,
        8: <Final />,
    }

    return (
        <div className={classes.root}>
            <div className={classes.frame}>
                {!isMobile && (
                    <IconButton className={classes.navBtn} onClick={goPrev} aria-label="Anterior">
                        <ArrowLeft fontSize="large" />
                    </IconButton>
                )}

                <div
                    className={classes.phone}
                    onMouseEnter={() => !isMobile && setPausado(true)}
                    onMouseLeave={() => !isMobile && setPausado(false)}
                    onMouseDown={() => !isMobile && setPausado(true)}
                    onMouseUp={() => !isMobile && setPausado(false)}
                    onTouchStart={onTouchStart}
                    onTouchEnd={onTouchEnd}
                >
                    <div className={classes.overlayTop}>
                        <div className={classes.barras}>{barras}</div>
                    </div>

                    <div className={classes.content}>{telasRender[index]}</div>

                    <div className={classes.tapZones}>
                        <div className={classes.leftZone} onClick={goPrev} aria-label="Voltar" />
                        <div className={classes.rightZone} onClick={goNext} aria-label="Avançar" />
                    </div>
                </div>

                {!isMobile && (
                    <IconButton className={classes.navBtn} onClick={goNext} aria-label="Próximo">
                        <ArrowRight fontSize="large" />
                    </IconButton>
                )}
            </div>
        </div>
    )
}

export default ResultadosAnual
